import React, {useState} from 'react';
import {useTranslation} from "react-i18next";
import {IonAlert} from "@ionic/react";
import Modal from "../../../components/modal/modal.component";
import {StyledButton, StyledWiteButton} from "@components/button/button.style";
import {StyledFooter} from "@components/pane/pane.style";
import {StyledModalContent} from "@components/modal/modal.style";
import {StyledDataTable} from "@components/content/content.style";
import moment from "moment";
import {Proposal} from "@models/calendar";
import {acceptProposal, rejectProposal} from "@services/calendar/proposal.service";
import {useToast} from "@context/toast.context";
import ProposalRejectModal from "@app/availabilityPage/modals/proposalRejectModal.component";

type EventPreviewModalProps = {
    proposal: Proposal;
    isOpen: boolean;
    onDone: () => void,
    onClose: () => void;
};

const getErrorCode = (response: any, error?: any): string | null => {
    if (response?.data?.result === 'error') {
        return response.data.message ?? 'UNKNOWN';
    }

    if (error) {
        return error?.response?.data?.message ?? 'UNKNOWN';
    }

    return null;
};

const getErrorTranslationKey = (code: string | null, fallback: string): string => {
    switch (code) {
        case 'PROPOSAL_HAS_ALREADY_STARTED':
            return 'proposal.error.hasAlreadyStarted';
        case 'NOT_AVAILABLE':
            return 'proposal.error.notAvailable';
        default:
            return fallback;
    }
};

const EventPreviewModal: React.FC<EventPreviewModalProps> = ({isOpen, proposal, onClose, onDone}: EventPreviewModalProps) => {
    const {t} = useTranslation();
    const {showToast} = useToast();

    const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
    const [showPlaceAdditionAlert, setShowPlaceAdditionAlert] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const status = proposal.attributes.status;
    const canAccept = status === 'PROPOSED';
    const canReject = status === 'PROPOSED' || status === 'ACCEPTED';

    const showError = (key: string) => {
        showToast({
            message: t(key),
            type: 'danger',
            duration: 3000,
        });
    };

    const handleProposalAccept = async (confirmPlaceAddition: boolean | null = null) => {
        if (submitting) {
            return;
        }

        setSubmitting(true);

        try {
            const response = await acceptProposal(proposal.id, confirmPlaceAddition);
            const errorCode = getErrorCode(response);

            if (errorCode) {
                if (errorCode === 'PLACE_NOT_PREFERRED' && !confirmPlaceAddition) {
                    setShowPlaceAdditionAlert(true);

                    return;
                }

                showError(getErrorTranslationKey(errorCode, 'proposal.error.accept'));

                return;
            }

            onClose();
            onDone();
        } catch (error) {
            showError(getErrorTranslationKey(getErrorCode(null, error), 'proposal.error.accept'));

            console.error(error);
        } finally {
            setSubmitting(false);
        }
    }

    const handleProposalReject = async (reason: string, replacement: string | null) => {
        if (submitting) {
            return;
        }

        setSubmitting(true);

        try {
            const response = await rejectProposal(proposal.id, reason, replacement);
            const errorCode = getErrorCode(response);

            if (errorCode) {
                showError(getErrorTranslationKey(errorCode, 'proposal.error.reject'));

                return;
            }

            setIsRejectModalOpen(false);
            onClose();
            onDone();
        } catch (error) {
            showError(getErrorTranslationKey(getErrorCode(null, error), 'proposal.error.reject'));

            console.error(error);
        } finally {
            setSubmitting(false);
        }
    }

    const placeName = proposal.attributes.placeOfService;
    const placeAddress = proposal.attributes.placeOfServiceAddress;

    return (
        <>
            <ProposalRejectModal
                isOpen={isRejectModalOpen}
                onClose={() => setIsRejectModalOpen(false)}
                onConfirm={(reason, replacement) => handleProposalReject(reason, replacement)}
            />
            <IonAlert
                isOpen={showPlaceAdditionAlert}
                onDidDismiss={() => setShowPlaceAdditionAlert(false)}
                header={t('proposal.placeNotPreferred.title')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPlaceAdditionAlert(false);
                        }
                    },
                    {
                        text: t('common.ok'),
                        handler: async () => {
                            await handleProposalAccept(true);
                        }
                    },
                ]}
            />
            <Modal initialBreakpoint={0.6} breakpoints={[0, 0.6]} isOpen={isOpen} onDidDismiss={() => onClose()}>
                <StyledModalContent className="scrollable">
                    <h6 className="description">{t('notificationTab.timetablePane.preview.title', {coordinator: proposal.attributes.coordinator})}</h6>
                    <StyledDataTable>
                        <div className="data-row">
                            <div className="data-title">{t('notificationTab.timetablePane.preview.place')}</div>
                            <div className="data-value">
                                {
                                    placeAddress
                                        ? <div className="data-icon-title titles" style={{padding: 0}}>
                                            <div className="main-title">{placeAddress}</div>
                                            <div className="sub-title">{placeName}</div>
                                        </div>
                                        : (placeName || '--')
                                }
                            </div>
                            <div className="data-action"></div>
                        </div>
                        <div className="data-row">
                            <div className="data-title">{t('notificationTab.timetablePane.preview.jobPosition')}</div>
                            <div className="data-value long-text">{proposal.attributes.jobPosition || '--'}</div>
                            <div className="data-action"></div>
                        </div>
                        <div className="data-row">
                            <div className="data-title">{t('notificationTab.timetablePane.preview.from')}</div>
                            <div
                                className="data-value">{moment(proposal.attributes.originalStartTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')}</div>
                            <div className="data-action"></div>
                        </div>
                        <div className="data-row">
                            <div className="data-title">{t('notificationTab.timetablePane.preview.to')}</div>
                            <div
                                className="data-value">{moment(proposal.attributes.originalEndTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')}</div>
                            <div className="data-action"></div>
                        </div>
                        <div className="data-row">
                            <div className="data-title">{t('notificationTab.timetablePane.preview.details')}</div>
                            <div className="data-value">{proposal.attributes.description || '--'}</div>
                            <div className="data-action"></div>
                        </div>
                    </StyledDataTable>

                    <StyledFooter className="modal footer no-shadow">
                        {
                            canAccept &&
                            <div style={{width: '100%'}}>
                                <StyledButton
                                    className="accept"
                                    disabled={submitting}
                                    onClick={() => handleProposalAccept()}>
                                    {t('notificationTab.taskItem.accept')}
                                </StyledButton>
                                <StyledWiteButton
                                    disabled={submitting}
                                    onClick={() => setIsRejectModalOpen(true)}>
                                    {t('notificationTab.taskItem.dismiss')}
                                </StyledWiteButton>
                            </div>
                        }
                        {
                            !canAccept && canReject &&
                            <div style={{width: '100%', textAlign: 'center'}}>
                                <h6 className="description">{t('notificationTab.taskItem.accepted')}</h6>
                                <StyledWiteButton
                                    disabled={submitting}
                                    onClick={() => setIsRejectModalOpen(true)}>
                                    {t('notificationTab.taskItem.dismiss')}
                                </StyledWiteButton>
                            </div>
                        }
                        {
                            !canAccept && !canReject &&
                            <h6 className="description">{t('notificationTab.taskItem.dismissed')}</h6>
                        }
                    </StyledFooter>
                </StyledModalContent>
            </Modal>
        </>
    );
};

export default EventPreviewModal;
